/**
 * PURPOSE: Alta, edición o reemplazo de una alternativa (hasta 3 por tipo y tono) de un equipo (FTR-0014;
 *   corrección de usabilidad 2026-09-14).
 * RESPONSIBILITIES: Prioridad de información — COLOR PREDOMINANTE (único obligatorio) primero, luego la foto
 *   (opcional, con vista previa) y, al final, el COLOR COMPLEMENTARIO (opcional, colapsado por defecto y
 *   abierto solo al editar una opción que ya lo tiene). Guardar nunca exige abrir la sección complementaria,
 *   subir una foto ni desplazarse hasta el final: las acciones viven en un pie fijo siempre visible. En un
 *   alta la foto se elige antes de que exista la opción y el contenedor la sube en cuanto el backend
 *   devuelve el `idoption`; sobre una opción ya guardada la foto es una acción inmediata. No acepta HEX
 *   libre ni un tercer color; la descripción libre ya no se ofrece en esta UI. La vista previa de la foto se
 *   amplía con un clic sobre ella (FTR-0039).
 * COLLABORATORS: uniformOptions.utils.js, UniformSwatches.jsx, UniformPhotoLightbox.jsx, CompanyUniformsTab.jsx,
 *   uniformOptionsService.js (uploadUniformOptionPhoto/removeUniformOptionPhoto).
 * ROUTE: winscore/src/admin/AdminCompany/UniformOptionEditor.jsx
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiImage, FiTrash2, FiUploadCloud } from "react-icons/fi";

import { removeUniformOptionPhoto, uploadUniformOptionPhoto } from "../../services/uniformOptionsService.js";
import { EnlargeablePhoto } from "./UniformPhotoLightbox.jsx";
import UniformSwatches from "./UniformSwatches.jsx";
import {
  buildUniformLabel,
  canSubmitOptionForm,
  clearComplementary,
  columnLabel,
  complementaryStartsOpen,
  formFromOption,
  getUniformErrorMessage,
  optionPositionLabel,
  primaryOptionLabel,
  slotName,
  validateOptionForm,
} from "./uniformOptions.utils.js";

const PHOTO_ACCEPT = "image/jpeg,image/png,image/webp";
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** Valida la foto en el cliente antes de subirla; el backend vuelve a validarla (firma binaria incluida). */
const checkPhotoFile = (file) => {
  if (!PHOTO_TYPES.includes(file.type)) return "Selecciona una foto JPEG, PNG o WebP.";
  if (file.size > PHOTO_MAX_BYTES) return "La foto no puede superar 5 MB.";
  return null;
};

const ColorGrid = ({ catalog, value, onChange, disabledId, name }) => (
  <div className="grid grid-cols-5 gap-2 sm:grid-cols-7" role="radiogroup" aria-label={name}>
    {catalog.map((color) => {
      const selected = String(value) === String(color.id);
      const disabled = disabledId && String(disabledId) === String(color.id);
      return (
        <button
          key={color.id}
          type="button"
          role="radio"
          aria-checked={selected}
          disabled={disabled}
          onClick={() => onChange(String(color.id))}
          title={color.name}
          className={`flex flex-col items-center gap-1 rounded-lg border p-1.5 text-[11px] leading-tight transition ${
            selected ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-400"
          } disabled:cursor-not-allowed disabled:opacity-30`}
        >
          <UniformSwatches colors={[color]} size="lg" />
          <span className="text-center text-slate-700">{color.name}</span>
        </button>
      );
    })}
  </div>
);

/**
 * Foto (opcional). Sobre una opción ya guardada sube/reemplaza/quita de inmediato. En un alta todavía no hay
 * `idoption`: la foto queda "pendiente" con vista previa local y el contenedor la sube tras crear la opción.
 */
const PhotoField = ({ team, option, pendingFile, onPendingFileChange, busy, onChanged }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Vista previa derivada del archivo pendiente (sin estado propio); se libera al cambiar o al cerrar.
  const preview = useMemo(() => (pendingFile ? URL.createObjectURL(pendingFile) : null), [pendingFile]);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const disabled = uploading || busy;

  const openPicker = () => {
    if (disabled) return;
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.click();
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const invalid = checkPhotoFile(file);
    if (invalid) {
      toast.error(invalid);
      return;
    }

    // Alta: aún no existe la opción. Se guarda la foto para subirla justo después de crearla.
    if (!option) {
      onPendingFileChange(file);
      return;
    }

    setUploading(true);
    try {
      const updated = await uploadUniformOptionPhoto(team.idparticipant, option.id, file);
      onChanged(updated.option);
      toast.success(option.photoUrl ? "Foto reemplazada." : "Foto cargada.");
    } catch (error) {
      toast.error(getUniformErrorMessage(error, "No se pudo subir la foto."));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!option) {
      onPendingFileChange(null);
      return;
    }
    if (!window.confirm("¿Quitar la foto de esta opción? Los colores no se ven afectados.")) return;
    setUploading(true);
    try {
      const updated = await removeUniformOptionPhoto(team.idparticipant, option.id);
      onChanged(updated.option);
      toast.success("Foto quitada.");
    } catch (error) {
      toast.error(getUniformErrorMessage(error, "No se pudo quitar la foto."));
    } finally {
      setUploading(false);
    }
  };

  const shown = preview || option?.photoUrl || null;

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
          {shown ? (
            // FTR-0039: clic sobre la foto (también la vista previa aún no subida) para verla en grande.
            <EnlargeablePhoto photoUrl={shown} label={option ? primaryOptionLabel(option) : ""} className="h-full w-full">
              <img src={shown} alt="Foto de la opción" className="h-full w-full object-cover" />
            </EnlargeablePhoto>
          ) : (
            <FiImage className="text-2xl text-slate-300" aria-hidden="true" />
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openPicker}
            disabled={disabled}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiUploadCloud />
            {uploading ? "Subiendo..." : shown ? "Reemplazar foto" : "Subir foto"}
          </button>
          {shown && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiTrash2 /> Quitar
            </button>
          )}
          <input ref={inputRef} type="file" accept={PHOTO_ACCEPT} onChange={handleFile} className="hidden" disabled={disabled} />
        </div>
      </div>
      {!option && pendingFile && <p className="mt-1 text-xs text-slate-500">Se subirá al guardar la opción.</p>}
    </div>
  );
};

const UniformOptionEditor = ({ team, column, option, legacy, catalog, saving, onCancel, onSubmit, onOptionChanged }) => {
  const [form, setForm] = useState(() => formFromOption(option));
  const [touched, setTouched] = useState(false);
  const [liveOption, setLiveOption] = useState(option || null);
  const [pendingPhoto, setPendingPhoto] = useState(null);
  // Colapsada en un alta; abierta al editar una opción que ya tiene color complementario.
  const [showComplementary, setShowComplementary] = useState(() => complementaryStartsOpen(option));
  const byId = useMemo(() => new Map(catalog.map((color) => [String(color.id), color])), [catalog]);
  const primary = byId.get(String(form.primaryColorId)) || null;
  const secondary = byId.get(String(form.secondaryColorId)) || null;
  const errors = validateOptionForm(form);
  const canSubmit = canSubmitOptionForm(form, { busy: saving });
  const set = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = (event) => {
    event.preventDefault();
    setTouched(true);
    if (canSubmit) onSubmit(form, { pendingPhoto });
  };

  const removeComplementary = () => {
    // Solo limpia el complementario: el predominante, la foto y la opción quedan intactos.
    setForm((current) => clearComplementary(current));
    setShowComplementary(false);
  };

  const title = option
    ? `Editar ${slotName(column)}`
    : legacy
    ? "Reemplazar registro legacy"
    : `Nueva opción · ${slotName(column)}`;
  const editingSubtitle = option ? `${optionPositionLabel(option.order)} · ${primaryOptionLabel(option)}` : "";

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onCancel}>
      <form
        onSubmit={submit}
        onMouseDown={(mouseEvent) => mouseEvent.stopPropagation()}
        className="flex max-h-[92vh] w-full max-w-3xl flex-col rounded-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="uniform-option-editor-title"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div>
            <h3 id="uniform-option-editor-title" className="text-xl font-bold text-slate-800">{title}</h3>
            <p className="text-sm text-slate-500">
              {team.institution?.name || "Equipo"} · {columnLabel(column)}
            </p>
            {editingSubtitle && <p className="text-xs text-slate-400">{editingSubtitle}</p>}
            {legacy && (
              <p className="mt-1 text-xs text-amber-700">
                Registro legacy actual: “{legacy.label || "Sin texto"}”. Se reemplaza en el mismo slot.
              </p>
            )}
          </div>
          <button type="button" onClick={onCancel} className="rounded-full px-3 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100" aria-label="Cerrar">
            ×
          </button>
        </div>

        {/* Cuerpo desplazable: el pie de acciones queda siempre visible debajo. */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <fieldset>
            <legend className="mb-2 text-sm font-semibold text-slate-700">COLOR PREDOMINANTE *</legend>
            <ColorGrid catalog={catalog} value={form.primaryColorId} onChange={set("primaryColorId")} name="Color predominante" />
            {touched && errors.primaryColorId && <p className="mt-1 text-xs text-rose-600">{errors.primaryColorId}</p>}
          </fieldset>

          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <UniformSwatches colors={[primary, secondary]} size="lg" />
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Etiqueta generada</p>
              <p className="font-semibold text-slate-800">{buildUniformLabel(primary, secondary) || "Elige el color predominante"}</p>
            </div>
          </div>

          <fieldset>
            <legend className="mb-2 text-sm font-semibold text-slate-700">Foto de la camiseta (opcional)</legend>
            <PhotoField
              team={team}
              option={liveOption}
              pendingFile={pendingPhoto}
              onPendingFileChange={setPendingPhoto}
              busy={saving}
              onChanged={(updated) => {
                setLiveOption(updated);
                onOptionChanged?.(updated);
              }}
            />
          </fieldset>

          {/* Secundario y colapsado por defecto: el color complementario nunca es obligatorio. */}
          <div className="rounded-xl border border-dashed border-slate-200 p-3">
            {showComplementary ? (
              <fieldset>
                <legend className="mb-2 flex flex-wrap items-center gap-3 text-sm font-semibold text-slate-700">
                  COLOR COMPLEMENTARIO (OPCIONAL)
                  <button type="button" onClick={removeComplementary} className="text-xs font-normal text-blue-600 hover:underline">
                    Quitar color complementario
                  </button>
                </legend>
                <ColorGrid catalog={catalog} value={form.secondaryColorId} onChange={set("secondaryColorId")} disabledId={form.primaryColorId} name="Color complementario" />
                {errors.secondaryColorId && <p className="mt-1 text-xs text-rose-600">{errors.secondaryColorId}</p>}
              </fieldset>
            ) : (
              <>
                <button type="button" onClick={() => setShowComplementary(true)} className="text-sm font-semibold text-blue-700 hover:underline">
                  + Agregar color complementario (opcional)
                </button>
                <p className="mt-1 text-xs text-slate-500">Úsalo solo si la camiseta combina dos colores.</p>
              </>
            )}
          </div>
        </div>

        {/* Pie fijo: siempre visible mientras el cuerpo se desplaza, en escritorio y en anchos móviles. */}
        <div className="sticky bottom-0 flex shrink-0 justify-end gap-2 rounded-b-2xl border-t border-slate-200 bg-white px-6 py-3">
          <button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UniformOptionEditor;
